import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  Divider,
  Tabs,
  Tab,
  useTheme,
  Chip,
  MenuItem,
  InputAdornment,
  Avatar,
  LinearProgress,
} from "@mui/material";
import {
  LuDownload,
  LuSearch,
  LuFilter,
  LuCalendar,
  LuPlus,
  LuTrendingUp,
  LuWallet,
  LuCreditCard,
  LuChartBar,
  LuArrowUpRight,
  LuArrowDownRight,
  LuUsers,
  LuSettings,
  LuBell,
  LuChevronDown,
} from "react-icons/lu";
import { HiChevronRight } from "react-icons/hi";
import TransactionsUI from "./TransactionsUI";
import PaymentDialog from "./PaymentDialog";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Billing = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const balanceData = [
    { 
      type: "MC", 
      label: "Message Credits", 
      amount: "₹12,450", 
      color: "#25D366", 
      icon: LuTrendingUp,
      trend: "up",
      change: "+12%",
      usage: 75
    },
    { 
      type: "UC", 
      label: "User Credits", 
      amount: "₹8,290", 
      color: "#6366F1", 
      icon: LuUsers,
      trend: "up",
      change: "+8%",
      usage: 60
    },
    { 
      type: "AC", 
      label: "API Credits", 
      amount: "₹15,670", 
      color: "#F59E0B", 
      icon: LuCreditCard,
      trend: "down",
      change: "-3%",
      usage: 45
    },
    { 
      type: "BIC", 
      label: "Business Credits", 
      amount: "₹23,450", 
      color: "#EF4444", 
      icon: LuChartBar,
      trend: "up",
      change: "+15%",
      usage: 85
    },
    { 
      type: "SM", 
      label: "Service Credits", 
      amount: "₹5,320", 
      color: "#8B5CF6", 
      icon: LuSettings,
      trend: "up",
      change: "+5%",
      usage: 30
    },
    { 
      type: "Total", 
      label: "Total Balance", 
      amount: "₹65,180", 
      color: "#1F2937", 
      icon: LuWallet,
      trend: "up",
      change: "+9%",
      usage: 65
    },
  ];

  const filterOptions = [
    { value: "all", label: "All Transactions" },
    { value: "success", label: "Successful" },
    { value: "pending", label: "Pending" },
    { value: "failed", label: "Failed" },
  ];

  const recentActivities = [
    { action: "Credit Added", amount: "₹5,000", time: "2 hours ago", type: "credit" },
    { action: "API Usage", amount: "₹1,250", time: "5 hours ago", type: "debit" },
    { action: "Monthly Subscription", amount: "₹2,999", time: "1 day ago", type: "debit" },
    { action: "Refund Processed", amount: "₹750", time: "2 days ago", type: "credit" },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "#F8FAFC",
        minHeight: "100vh",
        p: { xs: 2, sm: 3, md: 4 },
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* Header Section */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        mb={4}
        gap={2}
      >
        <Box>
          <Typography
            variant="h4"
            fontWeight={700}
            color="#1F2937"
            sx={{ 
              mb: 1,
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
              background: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Billing Dashboard
          </Typography>
          <Typography 
            variant="body1" 
            color="#6B7280"
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Manage your payments, transactions, and account balance
          </Typography>
        </Box>
        <Box 
          display="flex" 
          alignItems="center" 
          color="#6B7280"
          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
        >
          <Typography fontSize="inherit" color="inherit">
            Home
          </Typography>
          <HiChevronRight size={16} />
          <Typography fontSize="inherit" color="#25D366" fontWeight={600}>
            Billing
          </Typography>
        </Box>
      </Box>

      {/* Stats Overview */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: 3, 
            p: 3, 
            background: 'linear-gradient(135deg, #25D366 0%, #22C35E 100%)',
            color: 'white',
            boxShadow: '0 8px 32px rgba(37, 211, 102, 0.2)',
          }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight={700} mb={1}>
                  ₹65,180
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Current Balance
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', p: 1.5 }}>
                <LuWallet size={24} />
              </Avatar>
            </Box>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, p: 3, boxShadow: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight={700} mb={1} color="#1F2937">
                  12
                </Typography>
                <Typography variant="body2" color="#6B7280">
                  This Month
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: '#EEF2FF', p: 1.5 }}>
                <LuTrendingUp size={24} color="#4F46E5" />
              </Avatar>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, p: 3, boxShadow: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight={700} mb={1} color="#1F2937">
                  ₹8,249
                </Typography>
                <Typography variant="body2" color="#6B7280">
                  Total Spent
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: '#FEF3C7', p: 1.5 }}>
                <LuCreditCard size={24} color="#D97706" />
              </Avatar>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, p: 3, boxShadow: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight={700} mb={1} color="#1F2937">
                  98%
                </Typography>
                <Typography variant="body2" color="#6B7280">
                  Success Rate
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: '#DCFCE7', p: 1.5 }}>
                <LuChartBar size={24} color="#16A34A" />
              </Avatar>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Enhanced Filters Section */}
      <Card sx={{ 
        borderRadius: 3, 
        p: 3, 
        boxShadow: 2,
        mb: 4  // Added margin bottom to prevent overlapping
      }}>
        <Typography variant="h6" fontWeight={600} color="#1F2937" mb={3}>
          Export & Reports
        </Typography>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              label="Filter Type"
              fullWidth
              size="small"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LuFilter size={18} color="#6B7280" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'white',
                },
              }}
            >
              {filterOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={6} md={5}>
            <TextField
              label="Search"
              placeholder="Enter search term..."
              fullWidth
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LuSearch size={18} color="#6B7280" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'white',
                },
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              startIcon={<LuDownload size={18} />}
              fullWidth
              sx={{
                bgcolor: "#1F2937",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                py: 1,
                "&:hover": { 
                  bgcolor: "#374151",
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Export Report
            </Button>
          </Grid>
        </Grid>
      </Card>

      {/* Enhanced Tabs Section */}
      {/* <Card
        sx={{
          borderRadius: 3,
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          mb: 4,
          overflow: 'visible',
          background: 'white',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{
              px: { xs: 2, sm: 3 },
              '& .MuiTabs-indicator': {
                backgroundColor: '#25D366',
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                color: '#6B7280',
                py: 3,
                px: { xs: 1, sm: 2 },
                minWidth: { xs: 'auto', sm: 160 },
                minHeight: 'auto',
                '&.Mui-selected': {
                  color: '#25D366',
                },
                '&:hover': {
                  color: '#25D366',
                  backgroundColor: 'rgba(37, 211, 102, 0.04)',
                },
              },
            }}
          >
            {/*<Tab 
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <LuWallet size={18} />
                  <span>Balance Overview</span>
                </Box>
              } 
            />
            <Tab 
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <LuCreditCard size={18} />
                  <span>Transactions</span>
                </Box>
              } 
            />
          </Tabs>
        </CardContent>
      </Card> */}

      {/* Content Section */}
      {activeTab === 1 ? (
        <Grid container spacing={3}>
          {/* Left Column - Balance Cards */}
          <Grid item xs={12} lg={8}>
            <Card sx={{ borderRadius: 3, p: 3, boxShadow: 2, mb: 3 }}>
              <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" mb={3}>
                <Typography variant="h5" fontWeight={700} color="#1F2937">
                  Credit Balance
                </Typography>
                
                {/* Date Range Picker */}
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: 2,
                    px: 2,
                    py: 1.5,
                    bgcolor: "white",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    minWidth: { xs: '100%', sm: 280 },
                    mt: { xs: 2, sm: 0 },
                  }}
                >
                  <LuCalendar size={18} color="#6B7280" style={{ marginRight: 8 }} />
                  <input
                    type="text"
                    placeholder="Start date"
                    style={{
                      border: "none",
                      outline: "none",
                      width: "100%",
                      color: "#6B7280",
                      background: "transparent",
                      fontFamily: "Inter, sans-serif",
                      fontSize: "0.875rem",
                    }}
                  />
                  <Typography mx={1} color="#9CA3AF" fontSize="0.875rem">
                    →
                  </Typography>
                  <input
                    type="text"
                    placeholder="End date"
                    style={{
                      border: "none",
                      outline: "none",
                      width: "100%",
                      color: "#6B7280",
                      background: "transparent",
                      fontFamily: "Inter, sans-serif",
                      fontSize: "0.875rem",
                    }}
                  />
                </Box>
              </Box>

              {/* Enhanced Balance Cards */}
              <Grid container spacing={2}>
                {balanceData.map((item, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        p: 2.5,
                        transition: "all 0.3s ease-in-out",
                        border: `1px solid #F3F4F6`,
                        position: 'relative',
                        overflow: 'hidden',
                        "&:hover": { 
                          transform: 'translateY(-2px)',
                          boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                        <Box 
                          sx={{ 
                            backgroundColor: `${item.color}15`,
                            borderRadius: 2,
                            p: 1,
                          }}
                        >
                          <item.icon size={20} color={item.color} />
                        </Box>
                        <Chip 
                          label={
                            <Box display="flex" alignItems="center" gap={0.5}>
                              {item.trend === "up" ? <LuArrowUpRight size={14} /> : <LuArrowDownRight size={14} />}
                              {item.change}
                            </Box>
                          }
                          size="small" 
                          sx={{ 
                            backgroundColor: item.trend === "up" ? '#DCFCE7' : '#FEE2E2',
                            color: item.trend === "up" ? '#166534' : '#DC2626',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }} 
                        />
                      </Box>
                      
                      <Typography variant="h5" fontWeight={700} color="#1F2937" mb={1}>
                        {item.amount}
                      </Typography>
                      <Typography variant="body2" color="#6B7280" mb={2}>
                        {item.label}
                      </Typography>
                      
                      <Box display="flex" alignItems="center" gap={1}>
                        <LinearProgress 
                          variant="determinate" 
                          value={item.usage} 
                          sx={{ 
                            flex: 1, 
                            height: 6, 
                            borderRadius: 3,
                            backgroundColor: '#F3F4F6',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: item.color,
                              borderRadius: 3,
                            }
                          }} 
                        />
                        <Typography variant="caption" color="#6B7280" fontWeight={600}>
                          {item.usage}%
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Card>
          </Grid>

          {/* Right Column - Quick Actions & Recent Activity */}
          <Grid item xs={12} lg={4}>
            {/* Quick Actions */}
            <Card sx={{ borderRadius: 3, p: 3, boxShadow: 2, mb: 3 }}>
              <Typography variant="h6" fontWeight={600} color="#1F2937" mb={3}>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<LuPlus size={18} />}
                    sx={{
                      bgcolor: "#25D366",
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                      py: 1.5,
                      "&:hover": {
                        bgcolor: "#22C35E",
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    Add Funds
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<LuDownload size={18} />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                      py: 1.5,
                      borderColor: '#E5E7EB',
                      color: '#1F2937',
                      '&:hover': {
                        borderColor: '#25D366',
                        backgroundColor: 'rgba(37, 211, 102, 0.04)',
                      }
                    }}
                  >
                    Download Invoice
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<LuSettings size={18} />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                      py: 1.5,
                      borderColor: '#E5E7EB',
                      color: '#1F2937',
                      '&:hover': {
                        borderColor: '#25D366',
                        backgroundColor: 'rgba(37, 211, 102, 0.04)',
                      }
                    }}
                  >
                    Billing Settings
                  </Button>
                </Grid>
              </Grid>
            </Card>

            {/* Recent Activity */}
            <Card sx={{ borderRadius: 3, p: 3, boxShadow: 2 }}>
              <Typography variant="h6" fontWeight={600} color="#1F2937" mb={3}>
                Recent Activity
              </Typography>
              <Box 
                sx={{ 
                  maxHeight: 300, 
                  overflow: 'auto',
                  // Hide scrollbar for Chrome, Safari and Opera
                  '&::-webkit-scrollbar': {
                    display: 'none'
                  },
                  // Hide scrollbar for IE, Edge and Firefox
                  msOverflowStyle: 'none',  // IE and Edge
                  scrollbarWidth: 'none',   // Firefox
                }}
              >
                {recentActivities.map((activity, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      mb: 2, 
                      pb: 2, 
                      borderBottom: index < recentActivities.length - 1 ? '1px solid #F3F4F6' : 'none' 
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" fontWeight={600} color="#1F2937">
                        {activity.action}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        fontWeight={600} 
                        color={activity.type === 'credit' ? '#25D366' : '#EF4444'}
                      >
                        {activity.type === 'credit' ? '+' : '-'}{activity.amount}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="#6B7280">
                      {activity.time}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <>
          {/* Transactions Section */}
          <Box 
            display="flex" 
            flexDirection={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            mb={3}
            gap={2}
          >
            <Typography variant="h5" fontWeight={700} color="#1F2937">
              Transaction History
            </Typography>
            {/* <Button
              variant="contained"
              onClick={() => setIsDialogOpen(true)}
              startIcon={<LuPlus size={18} />}
              sx={{
                bgcolor: "#25D366",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                px: 3,
                py: 1.5,
                minWidth: { xs: '100%', sm: 'auto' },
                "&:hover": { 
                  bgcolor: "#22C35E",
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              New Transaction
            </Button> */}
          </Box>
          
          <TransactionsUI transactions={transactions} />
          
          <PaymentDialog
            isOpen={isDialogOpen}
            onClose={() => setIsDialogOpen(false)}
            addTransaction={(transaction) => {
              const txnWithMeta = {
                ...transaction,
                transaction_id: "TXN" + Date.now(),
                date_time: new Date().toLocaleString(),
              };
              setTransactions((prev) => [txnWithMeta, ...prev]);
              setIsDialogOpen(false);
            }}
          />
        </>
      )}
      
      <ToastContainer 
        position="bottom-right"
        toastStyle={{
          fontFamily: "'Inter', sans-serif",
          borderRadius: '12px',
        }}
      />
    </Box>
  );
};

export default Billing;